import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { DeleteButton } from 'pages/Shipments/components/ContainerActions';
import { SHIPMENTS_QUERY_PARAMS } from 'pages/Shipments/params';
import { deleteContainers } from 'pages/TrackAndTrace/components/controls/actions/utils';
import { useSelectionContext, useSelectionCount } from 'common/contexts/SelectionContext';
import * as React from 'react';
import { Routes } from 'constants/pages';
import { useSnackbar } from 'common/hooks/snackbar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSegment } from 'common/hooks/segment';
import { ExclamationCircleIcon, TrashIcon } from '@heroicons/react/20/solid';
import pluralize from 'pluralize';
import Toast from 'components/tailwind-ui/Toast';
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog';

interface Props {
  shouldDelete: boolean;
}

export const Delete: React.FC<Props> = (props) => {
  const { shouldDelete } = props;
  const { searchParams, setSearchParams } = useCustomSearchParams(SHIPMENTS_QUERY_PARAMS);
  const { selection } = useSelectionContext();
  const count = useSelectionCount(selection);

  const queryClient = useQueryClient();
  const displaySnackbar = useSnackbar();
  const shipmentPage = [Routes.ShipmentsActive, Routes.ShipmentsDeleted].includes(
    window.location.pathname as Routes
  );
  const { track } = useSegment();
  const [open, setOpen] = React.useState(false);

  let snackbarIcon = <TrashIcon className="text-gray-400 w-4 h-4" />;
  let snackbarTitle = `Successfully ${shouldDelete ? 'deleted' : 're-added'}!`;
  let snackbarDescription = `You have ${shouldDelete ? 'deleted' : 're-added'} ${count} ${pluralize(
    'container',
    count
  )} ${shouldDelete ? 'from' : 'to'} your watchlist.`;

  const renderHtml = (handleClose: () => void) => {
    return (
      <Toast
        handleClose={handleClose}
        icon={snackbarIcon}
        title={snackbarTitle}
        description={snackbarDescription}
      />
    );
  };

  const deleteMutation = useMutation(
    async ({
      containerNumbers,
      transactionIds,
    }: {
      containerNumbers: string[];
      transactionIds: string[];
    }) => {
      await deleteContainers(
        searchParams.containerNumbers,
        containerNumbers,
        transactionIds,
        { onlyShipments: !!shipmentPage, deleted: !shouldDelete, ...searchParams },
        setSearchParams,
        queryClient,
        shouldDelete
      );
    },
    {
      onSuccess: (_, variables) => {
        setOpen(false);
        displaySnackbar({
          useAlert: false,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          html: renderHtml,
        });

        track(
          `Container ${shouldDelete ? 'Deleted' : 'Re-added'}}`,
          {
            feature: 'watchlist',
            source: 'actionButton',
            nbContainers: variables.containerNumbers.length || variables.transactionIds.length,
            containers: variables,
          },
          { includeCompany: true, includeUser: true }
        );
      },
      onError: () => {
        setOpen(false);
        snackbarIcon = <ExclamationCircleIcon className="text-gray-400 w-4 h-4" />;
        snackbarTitle = `Error ${shouldDelete ? 'deleting' : 're-adding'}!`;
        snackbarDescription = `There was an error ${
          shouldDelete ? 'deleting' : 're-adding'
        } ${count} ${pluralize('container', count)} ${
          shouldDelete ? 'from' : 'to'
        } your watchlist.`;
        displaySnackbar({
          useAlert: false,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          html: renderHtml,
        });
      },
    }
  );

  const doMutation = () => {
    const containerNumbers =
      selection.model === 'page' ? selection.containers.map((c) => c.containerNumber) : [];
    const transactionIds =
      selection.model === 'page' ? selection.containers.map((c) => c.transaction) : [];
    deleteMutation.mutate({ containerNumbers, transactionIds });
  };

  return (
    <>
      <DeleteButton
        onClick={() => {
          if (shouldDelete) {
            setOpen(true);
          } else {
            // Do not show the dialog if we are re-adding/tracking containers
            doMutation();
          }
        }}
        shouldDelete={shouldDelete}
      />

      <DeleteConfirmationDialog
        open={open}
        onClose={() => setOpen(false)}
        onClick={doMutation}
        isLoading={deleteMutation.isLoading}
      />
    </>
  );
};
Delete.displayName = 'Shipments.Containers.ActionBar.Delete';
